const recoveryKey = 'people-evolution.route-module-reload'
const cooldownMs = 60_000

type RecoveryContext = {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  reload: () => void
  now: number
  online: boolean
}

/** Match module-loader failures without mistaking API or component errors for them. */
export function isRouteModuleLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS for|Loading (?:CSS )?chunk .+ failed/i.test(error.message)
}

/** A persisted guard survives the reload; blocked storage must never create a loop. */
export function recoverRouteModule(context?: RecoveryContext): boolean {
  try {
    if (!context && typeof window === 'undefined') return false
    const { storage, reload, now, online } = context ?? {
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
      now: Date.now(),
      online: window.navigator.onLine,
    }
    if (!online) return false
    const previous = storage.getItem(recoveryKey)
    if (previous !== null && Number.isFinite(Number(previous)) && now - Number(previous) < cooldownMs) return false
    storage.setItem(recoveryKey, String(now))
    reload()
    return true
  } catch {
    return false
  }
}

export async function importRouteModule<T>(factory: () => Promise<T>, recover: () => void = recoverRouteModule): Promise<T> {
  try {
    return await factory()
  } catch (error) {
    if (isRouteModuleLoadError(error)) recover()
    throw error
  }
}
